import { getSceneChanges } from '@goldcast/api/backend';
import { cronSpeakerFaceCoordinatesCreate, SpeakerFaceCoordinatesResponse } from '@goldcast/api/content';
import { Clip } from '../types/clip-types';
import { loadClip } from '../retrievers/loadClip';
import { FeatureFlagKeys, FeatureFlags } from './FeatureFlags';
import { ClipAvailabilityStatus } from '../types/clip-availability';
import { getClipAvailabilityStatus } from '../utils/clipAvailabilityUtils';
import { ContentMetadataStoreFactoryType } from '../stores/contentMetadataStoreFactory';
import { Word } from '../types/transcript-types';
import { prefetchDetailsForRemotionConfig } from '../retrievers/prefetchDetailsForRemotionConfig';
import { getAlignedRecordings } from '../utils/alignedRecordings';
import {
  getAllCompositeSegments,
  getClipBroadcastId,
  isClipAudiogram,
  isClipFromRecordingStudio,
  isClipLiveEvent,
  isConfigGenerationNeeded,
  normalizeClipConfigForDeleteFlag,
  shouldFetchSceneOverrides
} from '../utils/clipUtils';
import { initializeDeletesAndMaxVideoDuration, updateIntroOutroDurationHandler } from '../utils/assetMetadataUtils';
import { migrateClip } from '../utils/migrateClip';
import { SceneChanges } from '../types/scene-changes';
import { applyDefaultStylesToRS } from '../utils/applyDefaultStylesToRS';
import { updateClipDefaultProperties } from '../utils/updateClipDefaultProperties';
import { getPlayerConfig } from '../generators/config';
import { isHighlightReelClip } from '../generators/highlightReel/reelSegments';
import { BrandKitStore, PlatformBrandKitStore } from '../types/brand-kit-types';
import { finalizeClip } from '../utils/finalizeClip';
import { prepareClipForFinalSave } from '../utils/prepareClipForFinalSave';
import { BaseLogger, NoOpLogger } from './Logger';
import { PlayerConfigLogEvent } from '../constants/logEvents';

interface BaseInitClipInputProps {
  clipId: string;
  isSharedPage: boolean;
  isSavedSearchPage: boolean;
  isSmartEventsPage: boolean;
  contentMetadataStoreInstance: ContentMetadataStoreFactoryType;
  logger?: BaseLogger;
}

export abstract class BaseInitClip {
  protected props: BaseInitClipInputProps;
  protected clip: Clip | null;
  private featureFlagInstance: FeatureFlags | null;
  protected logger: BaseLogger;

  constructor(props: BaseInitClipInputProps) {
    this.props = props;
    this.clip = null;
    this.featureFlagInstance = null;
    this.logger = props.logger ?? new NoOpLogger();
  }

  protected abstract onAvailabilityStatus(status: ClipAvailabilityStatus): Promise<void>;
  protected abstract fetchVideoDuration(url: string): Promise<number>;
  protected abstract getAuthHeader(): string;
  protected abstract onSpeakerAnalsysisFetchFail(error: any): void;
  protected abstract onErrorToast(content: string): void;
  protected abstract onFullTranscript(transcript: Word[]): void;
  protected abstract logEvent(clip: Clip, name: string): void;
  protected abstract infoToast(content: string): void;
  protected abstract getBrandkitValues(): {
    clipBrandKitStore: BrandKitStore;
    brandKitStore: BrandKitStore;
    platformBrandKit: PlatformBrandKitStore;
  };
  protected abstract getZoomLevel(): number;
  protected abstract getPreviousClip(): Clip;
  protected abstract updateCurrentClipStore(clip: Clip): void;
  protected abstract initializeHistory(clip: Clip): void;
  protected abstract updateAllClipStore(clip: Clip): void;
  protected abstract persistClip(props: { clip: Clip; version: number }): Promise<void>;

  private async fetchClip(): Promise<Clip> {
    if (this.clip === null) {
      this.clip = await loadClip(this.props.clipId);
      this.logger.info(
        {
          event: PlayerConfigLogEvent.CLIP_FETCHED,
          clipId: this.props.clipId,
          org_id: this.getClip().organization
        },
        'Clip fetched'
      );
    }
    return this.clip as Clip;
  }

  private getClip(): Clip {
    if (this.clip === null) {
      throw new Error('clip is null, You need to call fetchClip first!');
    }
    return this.clip as Clip;
  }

  private getFlagInstance(): FeatureFlags {
    if (this.featureFlagInstance === null) {
      throw new Error('Feature flag needs to be instantiated first');
    }
    return this.featureFlagInstance;
  }

  public setFlagInstance(flags: FeatureFlags): void {
    this.featureFlagInstance = flags;
  }

  public async getClipOrganization(): Promise<string> {
    await this.fetchClip();
    return this.getClip().content.organization;
  }

  private getRSTestDate(): number {
    return this.getFlagInstance().getNumberFlag(FeatureFlagKeys.Use_CL_Test_RS_Date);
  }

  private getFlag(flagName: FeatureFlagKeys): boolean {
    return !!this.getFlagInstance().getFlag(flagName);
  }

  private checkClipAvailabilityStatus(): ClipAvailabilityStatus {
    const status = getClipAvailabilityStatus({
      clip: this.getClip(),
      isSharedPage: this.props.isSharedPage,
      rsTestDate: this.getRSTestDate()
    });
    this.logger.info({ event: PlayerConfigLogEvent.CLIP_AVAILABILITY_STATUS, status }, 'Clip availability checked');
    return status;
  }

  private async fetchClipRelatedData() {
    const { isSharedPage, isSmartEventsPage, isSavedSearchPage, contentMetadataStoreInstance } = this.props;
    await prefetchDetailsForRemotionConfig({
      clip: this.getClip(),
      isSharedPage,
      isSmartEventsPage,
      isSavedSearchPage,
      AuthHeader: this.getAuthHeader(),
      onSpeakerAnalsysisFetchFail: this.onSpeakerAnalsysisFetchFail.bind(this),
      contentMetadataStoreInstance,
      onErrorToast: this.onErrorToast.bind(this),
      onFullTranscript: this.onFullTranscript.bind(this),
      isFullVideoFaceRecFeatureFlag: this.getFlag(FeatureFlagKeys.Use_CL_Fullvideo_Facerec),
      isFullVideoFaceRecForPreRecordedFeatureFlag: this.getFlag(FeatureFlagKeys.Use_CL_Fullvideo_Facerec_Pre_Recorded),
      isScreenshareDetectionEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Screenshare_Detection),
      logger: this.logger
    });
  }

  private async initializeMaxDurationAndDeletes(): Promise<{ maxVideoDuration: number | null }> {
    const recordings = getAlignedRecordings({
      broadcastId: getClipBroadcastId(this.getClip()),
      contentMetadataStoreInstance: this.props.contentMetadataStoreInstance
    });
    const { maxVideoDuration, deletes } = await initializeDeletesAndMaxVideoDuration({
      clip: this.getClip(),
      recordings,
      fetchVideoDuration: this.fetchVideoDuration.bind(this)
    });
    if (maxVideoDuration !== null) {
      this.getClip().asset_metadata.maxVideoDuration = maxVideoDuration;
    }
    if (deletes !== null) {
      this.getClip().asset_metadata.deletes = deletes;
    }
    return { maxVideoDuration };
  }

  private initContentMetadataMediaSource() {
    this.props.contentMetadataStoreInstance.getState().initContentMediaSource({
      contentId: getClipBroadcastId(this.getClip())!,
      clip: this.getClip()
    });
  }

  private migrateClip(): { hasMigratedClip: boolean } {
    const { isSharedPage, contentMetadataStoreInstance } = this.props;
    const { hasMigratedClip, clip } = migrateClip({
      clip: this.getClip(),
      isFullFaceRecEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Fullvideo_Facerec),
      isFullFaceRecForPreRecordedEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Fullvideo_Facerec_Pre_Recorded),
      isSplitsTranslationEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Splits_Translations),
      isSharedPage,
      logEvent: this.logEvent.bind(this),
      contentMetadataStoreInstance,
      infoToast: this.infoToast.bind(this)
    });
    this.clip = clip;
    return { hasMigratedClip };
  }

  private async updateIntroAndOutroDuration(): Promise<{ hasUpdatedIntroOutroDurations: boolean }> {
    const { hasUpdatedIntroOutroDurations, clip } = await updateIntroOutroDurationHandler({
      clip: this.getClip(),
      fetchVideoDuration: this.fetchVideoDuration.bind(this)
    });
    this.clip = clip;
    return { hasUpdatedIntroOutroDurations };
  }

  private isConfigGenNeeded({
    maxVideoDuration,
    ...props
  }: {
    maxVideoDuration: number | null;
    hasMigratedClip: boolean;
    hasUpdatedIntroOutroDurations: boolean;
  }): boolean {
    const { isSharedPage } = this.props;
    return isConfigGenerationNeeded({
      clip: this.getClip(),
      isSharedPage,
      hasInitializedMaxVideoDuration: maxVideoDuration !== null,
      logEvent: this.logEvent.bind(this),
      useClInitNewPlayerConfig: this.getFlag(FeatureFlagKeys.Use_CL_Init_New_Player_Config),
      isSplitsTranslationEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Splits_Translations),
      ...props
    });
  }

  private async fetchSceneOverrides(): Promise<SceneChanges | undefined> {
    if (
      shouldFetchSceneOverrides({
        clip: this.getClip(),
        isSplitsTranslationEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Splits_Translations)
      })
    ) {
      return (await getSceneChanges({ id: this.getClip().broadcast })) as any as SceneChanges;
    }
    return undefined;
  }

  private async fetchFaceDefaultPositions({
    sceneOverrides
  }: {
    sceneOverrides: SceneChanges | undefined;
  }): Promise<SpeakerFaceCoordinatesResponse | undefined> {
    if (
      (sceneOverrides || isClipLiveEvent(this.getClip())) &&
      this.getFlag(FeatureFlagKeys.Use_CL_Face_Rec_To_Default_Position)
    ) {
      return await cronSpeakerFaceCoordinatesCreate({
        body: {
          content_id: this.getClip().content.id,
          project_id: this.getClip().content.project.id
        }
      });
    }
    return undefined;
  }

  private updateDefaultProperties({
    facialRecognitionToDefaultPosition
  }: {
    facialRecognitionToDefaultPosition: SpeakerFaceCoordinatesResponse | undefined;
  }) {
    updateClipDefaultProperties({
      clip: this.getClip(),
      facialRecognitionToDefaultPosition,
      isSplitsTranslationEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Splits_Translations),
      isFullFaceRecEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Fullvideo_Facerec),
      isFullFaceRecForPreRecordedEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Fullvideo_Facerec_Pre_Recorded),
      contentMetadataStoreInstance: this.props.contentMetadataStoreInstance
    });
  }

  private updatePlayerConfig({
    sceneOverrides,
    facialRecognitionToDefaultPosition
  }: {
    sceneOverrides?: SceneChanges | undefined;
    facialRecognitionToDefaultPosition?: SpeakerFaceCoordinatesResponse | undefined;
  }) {
    const isPersonContainerEnabled = this.getFlag(FeatureFlagKeys.Use_CL_Person_Container);
    const playerConfig = getPlayerConfig({
      clip: this.getClip(),
      sceneOverrides,
      facialRecognitionToDefaultPosition,
      shouldUpdatePlayerConfig: true,
      isFullFaceRecEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Fullvideo_Facerec),
      isFullFaceRecForPreRecordedEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Fullvideo_Facerec_Pre_Recorded),
      isScreenshareDetectionEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Screenshare_Detection),
      isUploadsPreviewAudioDiscontinuityEnabled: this.getFlag(
        FeatureFlagKeys.Use_CL_Uploads_Preview_Audio_Discontinuity
      ),
      ...(typeof isPersonContainerEnabled === 'boolean' ? { isPersonContainerEnabled } : {}),
      isPlatformBrandKitEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Platform_Brandkit),
      isSplitsTranslationEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Splits_Translations),
      contentMetadataStoreInstance: this.props.contentMetadataStoreInstance,
      organization: this.getClip().content.organization,
      zoomLevel: this.getZoomLevel(),
      previousClip: this.getPreviousClip(),
      ...this.getBrandkitValues()
    });
    this.getClip().asset_metadata.player_config = playerConfig;
  }

  private updateAssetMetadataScenes({ sceneOverrides }: { sceneOverrides: SceneChanges | undefined }) {
    if (!sceneOverrides) {
      return;
    }
    const allCompositeSegments = getAllCompositeSegments(this.getClip());
    // Single-composite only: reel segments are each framed to their own source window, not the clip window.
    if (allCompositeSegments.length === 1) {
      const firstSegment = allCompositeSegments[0];
      this.getClip().asset_metadata.scenes = firstSegment?.scenes || this.getClip().asset_metadata.scenes;
    }
    // Flush sceneOverrides-derived scenes into segment_metadata so the final-save updatePlayerConfig({}) keeps them.
    allCompositeSegments.forEach(segment => {
      if (segment.segment_metadata) {
        segment.segment_metadata.scenes = segment.scenes ?? [];
      }
    });
  }

  private shouldUpdateInDb({
    shouldUpdatePlayerConfig,
    facialRecognitionToDefaultPosition
  }: {
    shouldUpdatePlayerConfig: boolean;
    facialRecognitionToDefaultPosition: SpeakerFaceCoordinatesResponse | undefined;
  }) {
    const { updateInDb } = finalizeClip({
      clip: this.getClip(),
      clipConfigUpdateOverride: shouldUpdatePlayerConfig,
      facialRecognitionToDefaultPosition,
      isSplitsTranslationEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Splits_Translations),
      isFullFaceRecEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Fullvideo_Facerec),
      isFullFaceRecForPreRecordedEnabled: this.getFlag(FeatureFlagKeys.Use_CL_Fullvideo_Facerec_Pre_Recorded),
      contentMetadataStoreInstance: this.props.contentMetadataStoreInstance
    });
    return { updateInDb };
  }

  // Overridden by content-studio to derive captions and default pause removal for a reel.
  // Returns whether anything was derived; the base implementation is a no-op.
  protected deriveReelCaptions(_clip: Clip): boolean {
    return false;
  }

  /**
   * Whether this reel still needs its defaults derived. A multi-window reel is a splits clip, and
   * an empty `segment_metadata.captions` is the marker that derivation has not run yet.
   *
   * A single-window reel renders through the non-splits path, whose composite carries no
   * `segment_metadata` at all — its captions live on the clip and BE usually sends them, so they
   * are not a usable marker. There, the pause-removal deletes are: they are what still has to be
   * applied. Probing `segment_metadata.captions` alone would report "missing" on every single load.
   */
  private shouldDeriveReelDefaults(): boolean {
    const clip = this.getClip();
    const segmentsWithMetadata = getAllCompositeSegments(clip).filter(segment => segment.segment_metadata);
    if (segmentsWithMetadata.length) {
      return segmentsWithMetadata.some(segment => !segment.segment_metadata?.captions?.length);
    }
    if (!clip.asset_metadata.captions?.length) {
      return true;
    }
    return !Object.values(clip.asset_metadata.deletes ?? {}).some(entry => entry.deleteType === 'PAUSE_OPTIMIZATION');
  }

  public async initializeClip(): Promise<{ clip: Clip; updateInDb: boolean }> {
    this.getFlagInstance();
    await this.fetchClip();
    const clipAvailabilityStatus = this.checkClipAvailabilityStatus();
    await this.onAvailabilityStatus(clipAvailabilityStatus);
    await this.fetchClipRelatedData();
    const { maxVideoDuration } = await this.initializeMaxDurationAndDeletes();
    this.initContentMetadataMediaSource();
    const { hasMigratedClip } = this.migrateClip();
    const { hasUpdatedIntroOutroDurations } = await this.updateIntroAndOutroDuration();
    const shouldUpdatePlayerConfig = this.isConfigGenNeeded({
      maxVideoDuration,
      hasMigratedClip,
      hasUpdatedIntroOutroDurations
    });
    let facialRecognitionToDefaultPosition: SpeakerFaceCoordinatesResponse | undefined = undefined;
    if (shouldUpdatePlayerConfig) {
      let sceneOverrides: SceneChanges | undefined = undefined;
      if (isClipFromRecordingStudio(this.getClip()) && !isClipAudiogram(this.getClip())) {
        this.clip = applyDefaultStylesToRS(this.getClip());
        sceneOverrides = await this.fetchSceneOverrides();
      }
      facialRecognitionToDefaultPosition = await this.fetchFaceDefaultPositions({ sceneOverrides });
      this.updateDefaultProperties({ facialRecognitionToDefaultPosition });
      this.updatePlayerConfig({ sceneOverrides, facialRecognitionToDefaultPosition });
      this.logger.info(
        {
          event: PlayerConfigLogEvent.PLAYER_CONFIG_GENERATED,
          playerConfig: this.getClip().asset_metadata.player_config
        },
        'Player config generated'
      );
      this.updateAssetMetadataScenes({ sceneOverrides });
    } else {
      this.logger.info({ event: PlayerConfigLogEvent.PLAYER_CONFIG_SKIPPED }, 'Player config generation skipped');
    }

    // Derive reel captions on first load and bake them into the PC, for every reel — a
    // single-window reel goes through the non-splits path but still needs captions and the
    // default pause removal. Subsequent loads skip this once the captions are in place.
    let shouldUpdateCaptionsInDb = false;
    if (isHighlightReelClip(this.getClip()) && this.shouldDeriveReelDefaults()) {
      // Only claim a DB update when derivation actually changed something — otherwise a reel that
      // cannot derive, or has no pauses to trim, would persist a new version on every load.
      shouldUpdateCaptionsInDb = this.deriveReelCaptions(this.getClip());
      // Config gen was skipped (BE-generated PC), so rebuild here to pick up the derived captions
      // and the pause-removal deletes; when it ran, the final-save rebuild below covers them.
      if (shouldUpdateCaptionsInDb && !shouldUpdatePlayerConfig) {
        this.updatePlayerConfig({});
      }
    }
    const { updateInDb } = this.shouldUpdateInDb({ shouldUpdatePlayerConfig, facialRecognitionToDefaultPosition });
    // Also persist when captions were derived for the first time so subsequent loads skip re-derivation for highlight reels.
    const finalUpdateInDb = updateInDb || shouldUpdateCaptionsInDb;
    this.clip = normalizeClipConfigForDeleteFlag(this.getClip());
    this.updateCurrentClipStore(this.getClip());
    this.initializeHistory(this.getClip());
    if (finalUpdateInDb && !this.props.isSharedPage) {
      const { finalClip, version } = prepareClipForFinalSave(this.getClip());
      this.clip = finalClip;
      this.getClip().asset_metadata.remotionConfig = undefined as any;
      this.updatePlayerConfig({});
      this.updateCurrentClipStore(this.getClip());
      this.updateAllClipStore(this.getClip());
      await this.persistClip({ clip: this.getClip(), version });
      this.logger.info(
        { event: PlayerConfigLogEvent.CLIP_PERSISTED, clipId: this.getClip().id },
        'Clip persisted to DB'
      );
    }
    this.logger.info(
      { event: PlayerConfigLogEvent.PLAYER_CONFIG_RESPONSE, clip: this.getClip(), updateInDb: finalUpdateInDb },
      'Player config response'
    );
    return { clip: this.getClip(), updateInDb: finalUpdateInDb };
  }
}
